import Sortable from "sortablejs";
import { haversineKm } from "./distance";
import { openEditDialog } from "./edit-dialog";
import { debounce, describeResult, pickCityName, searchCity } from "./geocode";
import { icon, type IconName } from "./icons";
import { i18n } from "./i18n";
import { focusStop } from "./map";
import {
  buildShareUrl,
  buildWorkerShareUrl,
  encodeTripToHash,
  saveTripToWorker,
  updateTripOnWorker,
} from "./share-url";
import { openSavedTripsDialog } from "./saved-trips-dialog";
import { openShortcutsDialog } from "./shortcuts";
import { stops } from "./stops";
import { openSummaryDialog } from "./summary";
import { theme } from "./theme";
import { estimateMinutes, formatDuration } from "./travel-time";
import { parseTripJson } from "./trip-import";
import {
  TRAVEL_MODE_ICON_NAME,
  type NominatimSearchResult,
  type Stop,
} from "./types";

const DRAWER_BREAKPOINT = 900;

type MenuActionId = "savedTrips" | "summary" | "share" | "export" | "import" | "help";

type MenuItem = {
  el: HTMLButtonElement;
  label: HTMLElement;
};

type Elements = {
  searchInput: HTMLInputElement;
  searchResults: HTMLUListElement;
  stopList: HTMLUListElement;
  emptyHint: HTMLElement;
  menuBtn: HTMLButtonElement;
  menuPopover: HTMLElement;
  menuItems: Record<MenuActionId, MenuItem>;
  importFile: HTMLInputElement;
  langToggle: HTMLButtonElement;
  langCurrent: HTMLElement;
  themeToggle: HTMLButtonElement;
  collapseBtn: HTMLButtonElement;
  railMini: HTMLButtonElement;
  railMiniName: HTMLElement;
  railMiniCount: HTMLElement;
  railMiniIcon: HTMLElement;
  searchIcon: HTMLElement;
  tripDates: HTMLElement;
};

let pendingSearch: AbortController | null = null;
let shareResetTimer: ReturnType<typeof setTimeout> | null = null;
const expandedStopIds = new Set<string>();

export function initSidebar(): void {
  const els: Elements = {
    searchInput: required<HTMLInputElement>("#search-input"),
    searchResults: required<HTMLUListElement>("#search-results"),
    stopList: required<HTMLUListElement>("#stop-list"),
    emptyHint: required<HTMLElement>("#empty-hint"),
    menuBtn: required<HTMLButtonElement>("#menu-btn"),
    menuPopover: required<HTMLElement>("#trip-menu"),
    menuItems: {
      savedTrips: menuItemRefs("savedTrips"),
      summary: menuItemRefs("summary"),
      share: menuItemRefs("share"),
      export: menuItemRefs("export"),
      import: menuItemRefs("import"),
      help: menuItemRefs("help"),
    },
    importFile: required<HTMLInputElement>("#import-file"),
    langToggle: required<HTMLButtonElement>("#lang-toggle"),
    langCurrent: required<HTMLElement>("#lang-current"),
    themeToggle: required<HTMLButtonElement>("#theme-toggle"),
    collapseBtn: required<HTMLButtonElement>("#collapse-btn"),
    railMini: required<HTMLButtonElement>("#rail-mini"),
    railMiniName: required<HTMLElement>("#rail-mini-name"),
    railMiniCount: required<HTMLElement>("#rail-mini-count"),
    railMiniIcon: required<HTMLElement>("#rail-mini-icon"),
    searchIcon: required<HTMLElement>("#search-icon"),
    tripDates: required<HTMLElement>("#trip-dates"),
  };

  wireSearch(els);
  wireSortable(els.stopList);
  wireMenu(els);
  wireLangToggle(els);
  wireThemeToggle(els);
  wireRailCollapse(els);
  setupSidebarToggle();
  initTripName();
  els.searchIcon.appendChild(icon("search", { size: 17 }));
  stops.addEventListener("change", () => {
    renderList(els);
    renderTripDates(els);
    renderRailMiniCount(els);
  });
  i18n.addEventListener("change", () => {
    renderList(els);
    renderTripDates(els);
    els.langCurrent.textContent = i18n.current().toUpperCase();
  });
  els.langCurrent.textContent = i18n.current().toUpperCase();
  renderList(els);
  renderTripDates(els);
  renderRailMiniCount(els);
}

function wireThemeToggle(els: Elements): void {
  const refresh = (): void => {
    els.themeToggle.replaceChildren(icon(theme.current() === "dark" ? "moon" : "sun", { size: 15 }));
  };
  els.themeToggle.addEventListener("click", () => theme.toggle());
  theme.addEventListener("change", refresh);
  refresh();
}

function wireRailCollapse(els: Elements): void {
  const sidebar = required<HTMLElement>("#sidebar");
  els.collapseBtn.appendChild(icon("chevron-right", { size: 16 }));
  els.railMiniIcon.appendChild(icon("chevron-right", { size: 16 }));

  const setCollapsed = (collapsed: boolean): void => {
    sidebar.classList.toggle("collapsed", collapsed);
    els.railMini.classList.toggle("hidden", !collapsed);
  };

  els.collapseBtn.addEventListener("click", () => setCollapsed(true));
  els.railMini.addEventListener("click", () => setCollapsed(false));
}

function renderRailMiniCount(els: Elements): void {
  const n = stops.all().length;
  els.railMiniName.textContent = stops.getTripName() || i18n.t("tripNamePlaceholder");
  els.railMiniCount.textContent =
    n === 1 ? i18n.t("savedTripsStopCountOne") : i18n.t("savedTripsStopCount", { n });
}

function renderTripDates(els: Elements): void {
  const all = stops.all();
  const fmt = new Intl.DateTimeFormat(i18n.current(), { day: "numeric", month: "short" });
  const fromDates = all.map((s) => parseISODateLocal(s.dateFrom)).filter((d): d is Date => d != null);
  const toDates = all.map((s) => parseISODateLocal(s.dateTo)).filter((d): d is Date => d != null);

  if (fromDates.length === 0 && toDates.length === 0) {
    els.tripDates.hidden = true;
    return;
  }

  const parts: string[] = [];
  const earliest = fromDates.length ? new Date(Math.min(...fromDates.map((d) => d.getTime()))) : null;
  const latest = toDates.length ? new Date(Math.max(...toDates.map((d) => d.getTime()))) : null;

  if (earliest && latest) {
    parts.push(`${fmt.format(earliest)} – ${fmt.format(latest)}`);
    const nights = Math.round((latest.getTime() - earliest.getTime()) / 86_400_000);
    if (nights > 0) {
      parts.push(nights === 1 ? i18n.t("tripNightsOne") : i18n.t("tripNights", { n: nights }));
    }
  } else if (earliest) {
    parts.push(fmt.format(earliest));
  } else if (latest) {
    parts.push(fmt.format(latest));
  }

  parts.push(all.length === 1 ? i18n.t("savedTripsStopCountOne") : i18n.t("savedTripsStopCount", { n: all.length }));

  els.tripDates.textContent = parts.join(" · ");
  els.tripDates.hidden = false;
}

function menuItemRefs(action: MenuActionId): MenuItem {
  const el = required<HTMLButtonElement>(`[data-menu-action="${action}"]`);
  const label = el.querySelector<HTMLElement>(".menu-item-label") ?? el;
  return { el, label };
}

function wireLangToggle(els: Elements): void {
  els.langToggle.addEventListener("click", () => i18n.toggle());
}

function wireMenu(els: Elements): void {
  els.menuBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleMenu(els);
  });

  document.addEventListener("click", (e) => {
    if (!isMenuOpen(els)) return;
    const target = e.target as Node | null;
    if (target && (els.menuPopover.contains(target) || els.menuBtn.contains(target))) return;
    closeMenu(els);
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && isMenuOpen(els)) {
      closeMenu(els);
      els.menuBtn.focus();
    }
  });

  els.menuItems.savedTrips.el.addEventListener("click", () => {
    closeMenu(els);
    openSavedTripsDialog();
  });
  els.menuItems.summary.el.addEventListener("click", () => {
    closeMenu(els);
    openSummaryDialog();
  });
  els.menuItems.share.el.addEventListener("click", () => {
    void shareTrip(els);
  });
  els.menuItems.export.el.addEventListener("click", () => {
    closeMenu(els);
    exportTrip();
  });
  els.menuItems.import.el.addEventListener("click", () => {
    closeMenu(els);
    els.importFile.click();
  });
  els.menuItems.help.el.addEventListener("click", () => {
    closeMenu(els);
    openShortcutsDialog();
  });
  els.importFile.addEventListener("change", async () => {
    const file = els.importFile.files?.[0];
    els.importFile.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      const imported = parseTripJson(text);
      // Added as a new trip in the library (fresh id via replaceAll); the current trip is
      // already mirrored there, so nothing is overwritten.
      stops.replaceAll(imported.stops, imported.tripName);
    } catch (err) {
      alert(i18n.t("importFailed", { reason: (err as Error).message }));
    }
  });
}

function isMenuOpen(els: Elements): boolean {
  return !els.menuPopover.hidden;
}

function openMenu(els: Elements): void {
  els.menuPopover.hidden = false;
  els.menuBtn.setAttribute("aria-expanded", "true");
}

function closeMenu(els: Elements): void {
  els.menuPopover.hidden = true;
  els.menuBtn.setAttribute("aria-expanded", "false");
}

function toggleMenu(els: Elements): void {
  if (isMenuOpen(els)) closeMenu(els);
  else openMenu(els);
}

async function shareTrip(els: Elements): Promise<void> {
  const all = stops.all();
  if (all.length === 0) return;
  const tripName = stops.getTripName() || undefined;
  try {
    let url: string;
    try {
      url = buildWorkerShareUrl(await publishTrip(all, tripName));
    } catch {
      // Worker unavailable — fall back to hash encoding
      const encoded = await encodeTripToHash(all, tripName);
      url = buildShareUrl(encoded);
    }
    await navigator.clipboard.writeText(url);
    flashShareCopied(els, "shareCopied");
  } catch {
    flashShareCopied(els, "shareFailed");
  }
}

/**
 * Re-sharing keeps the same link: phones that already opened it pick the edit up instead of
 * needing a new one. Returns the trip id.
 *
 * A 403/404 means the link can no longer be written (it predates edit tokens, or it expired),
 * so we mint a fresh one rather than leaving the user unable to share at all.
 */
async function publishTrip(all: readonly Stop[], tripName?: string): Promise<string> {
  const existing = stops.getShare();
  if (existing) {
    try {
      await updateTripOnWorker(existing, all, tripName);
      return existing.id;
    } catch {
      stops.setShare(undefined);
    }
  }
  const share = await saveTripToWorker(all, tripName);
  stops.setShare(share);
  return share.id;
}

function flashShareCopied(els: Elements, key: "shareCopied" | "shareFailed"): void {
  const item = els.menuItems.share;
  item.label.textContent = i18n.t(key);
  item.el.classList.toggle("copied", key === "shareCopied");
  if (shareResetTimer) clearTimeout(shareResetTimer);
  shareResetTimer = setTimeout(() => {
    item.label.textContent = i18n.t("share");
    item.el.classList.remove("copied");
    shareResetTimer = null;
    closeMenu(els);
  }, 1500);
}

function wireSearch(els: Elements): void {
  const runSearch = debounce((query: string) => {
    pendingSearch?.abort();
    pendingSearch = new AbortController();
    searchCity(query, pendingSearch.signal)
      .then((results) => renderSearchResults(els, results))
      .catch((err) => {
        if ((err as Error).name === "AbortError") return;
        renderSearchError(els);
      });
  }, 300);

  els.searchInput.addEventListener("input", () => {
    const value = els.searchInput.value.trim();
    if (value.length < 2) {
      hideSearchResults(els);
      pendingSearch?.abort();
      return;
    }
    runSearch(value);
  });

  els.searchInput.addEventListener("focus", () => {
    if (els.searchResults.children.length > 0) {
      els.searchResults.hidden = false;
    }
  });

  document.addEventListener("click", (e) => {
    if (!els.searchInput.contains(e.target as Node) && !els.searchResults.contains(e.target as Node)) {
      hideSearchResults(els);
    }
  });

  els.searchInput.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      hideSearchResults(els);
      els.searchInput.blur();
    }
  });
}

function renderSearchResults(els: Elements, results: NominatimSearchResult[]): void {
  els.searchResults.replaceChildren();
  if (results.length === 0) {
    const li = document.createElement("li");
    li.className = "search-result empty";
    li.textContent = i18n.t("noMatches");
    els.searchResults.appendChild(li);
    els.searchResults.hidden = false;
    return;
  }
  for (const r of results) {
    const li = document.createElement("li");
    li.className = "search-result";
    li.tabIndex = 0;
    li.textContent = describeResult(r);
    li.addEventListener("click", () => addFromSearch(els, r));
    li.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        addFromSearch(els, r);
      }
    });
    els.searchResults.appendChild(li);
  }
  els.searchResults.hidden = false;
}

function renderSearchError(els: Elements): void {
  els.searchResults.replaceChildren();
  const li = document.createElement("li");
  li.className = "search-result empty";
  li.textContent = i18n.t("searchFailed");
  els.searchResults.appendChild(li);
  els.searchResults.hidden = false;
}

function hideSearchResults(els: Elements): void {
  els.searchResults.hidden = true;
}

function addFromSearch(els: Elements, result: NominatimSearchResult): void {
  const lat = parseFloat(result.lat);
  const lon = parseFloat(result.lon);
  const city = pickCityName(result.address) ?? result.name ?? result.display_name;
  const stop = stops.add({ name: city, city, lat, lon });
  focusStop(stop);
  els.searchInput.value = "";
  hideSearchResults(els);
  if (window.innerWidth < DRAWER_BREAKPOINT) {
    document.body.classList.remove("sidebar-open");
    const toggle = document.querySelector<HTMLButtonElement>("#sidebar-toggle");
    const scrim = document.querySelector<HTMLElement>("#sidebar-scrim");
    toggle?.setAttribute("aria-expanded", "false");
    if (scrim) scrim.hidden = true;
  }
}

function wireSortable(list: HTMLUListElement): void {
  Sortable.create(list, {
    handle: ".drag-handle",
    animation: 150,
    onEnd: (evt) => {
      if (evt.oldIndex === undefined || evt.newIndex === undefined) return;
      stops.reorder(evt.oldIndex, evt.newIndex);
    },
  });
}

function renderList(els: Elements): void {
  const all = stops.all();
  els.stopList.replaceChildren();
  els.emptyHint.hidden = all.length > 0;

  all.forEach((stop, index) => {
    const prev = index > 0 ? all[index - 1] : null;
    els.stopList.appendChild(renderStop(stop, prev));
  });
}

function renderStop(stop: Stop, prev: Stop | null): HTMLLIElement {
  const li = document.createElement("li");
  li.className = "stop-item";
  li.dataset.id = stop.id;

  const row = document.createElement("div");
  row.className = "stop-item-row";

  const handle = document.createElement("span");
  handle.className = "drag-handle";
  handle.appendChild(icon("drag", { size: 16 }));
  handle.title = i18n.t("dragToReorder");
  handle.setAttribute("aria-label", i18n.t("dragToReorder"));

  const main = document.createElement("button");
  main.type = "button";
  main.className = "stop-main";
  main.title = i18n.t("showOnMap");

  const name = document.createElement("span");
  name.className = "stop-name";
  name.textContent = stop.city || stop.name;

  main.append(name);

  const meta = renderStopMeta(stop, prev);
  if (meta) main.append(meta);

  main.addEventListener("click", () => focusStop(stop));

  const editBtn = iconButton("edit", i18n.t("rename"));
  editBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    openEditDialog(stop);
  });

  const deleteBtn = iconButton("trash", i18n.t("delete"));
  deleteBtn.classList.add("danger");
  deleteBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    stops.remove(stop.id);
  });

  const expanded = expandedStopIds.has(stop.id);
  const caretBtn = iconButton("chevron-down", i18n.t(expanded ? "stopCollapse" : "stopExpand"));
  caretBtn.classList.add("caret");
  if (expanded) caretBtn.classList.add("up");
  caretBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    if (expandedStopIds.has(stop.id)) expandedStopIds.delete(stop.id);
    else expandedStopIds.add(stop.id);
    li.replaceWith(renderStop(stop, prev));
  });

  row.append(handle, main, editBtn, deleteBtn, caretBtn);
  li.append(row);

  if (expanded) {
    const body = renderExpandedBody(stop);
    if (body) li.append(body);
    li.classList.add("expanded");
  }

  return li;
}

function renderExpandedBody(stop: Stop): HTMLElement {
  const body = document.createElement("div");
  body.className = "stop-item-expand";

  if (stop.hotel?.name) {
    const line = document.createElement("div");
    line.className = "xp-line";
    const roleIcon = document.createElement("span");
    roleIcon.className = "role-hotel";
    roleIcon.appendChild(icon("hotel", { size: 15 }));
    const bold = document.createElement("b");
    bold.textContent = stop.hotel.name;
    line.append(roleIcon, bold);
    if (stop.hotel.checkIn) {
      const muted = document.createElement("span");
      muted.className = "muted";
      muted.textContent = ` · check-in ${stop.hotel.checkIn}`;
      line.append(muted);
    }
    body.append(line);
  }

  if (stop.arriveDetails && (stop.arriveDetails.arrivalTime || stop.arriveDetails.carrier)) {
    body.append(renderTravelLine(stop, "arrive"));
  }
  if (stop.departDetails && (stop.departDetails.departureTime || stop.departDetails.carrier)) {
    body.append(renderTravelLine(stop, "depart"));
  }

  if (stop.notes) {
    const line = document.createElement("div");
    line.className = "xp-line";
    const muted = document.createElement("span");
    muted.className = "muted";
    muted.appendChild(icon("summary", { size: 15 }));
    line.append(muted);
    const text = document.createElement("span");
    text.className = "muted";
    text.textContent = stop.notes;
    line.append(text);
    body.append(line);
  }

  const tags = document.createElement("div");
  tags.className = "xp-tags";

  const placesCount = stop.places?.length ?? 0;
  const excursionsCount = stop.excursions?.length ?? 0;

  if (placesCount > 0) {
    tags.append(makeXpTag(i18n.t("stopPlacesChip"), placesCount, () => openEditDialog(stop, { initialTab: "places" })));
  }
  if (excursionsCount > 0) {
    tags.append(makeXpTag(i18n.t("stopExcursionsChip"), excursionsCount, () => openEditDialog(stop, { initialTab: "excursions" })));
  }
  const editAll = document.createElement("button");
  editAll.type = "button";
  editAll.className = "xp-tag";
  editAll.textContent = i18n.t("stopEditAll");
  editAll.addEventListener("click", (e) => {
    e.stopPropagation();
    openEditDialog(stop);
  });
  tags.append(editAll);

  body.append(tags);
  return body;
}

function makeXpTag(label: string, count: number, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "xp-tag";
  btn.append(`${label} `);
  const b = document.createElement("b");
  b.className = "num";
  b.textContent = String(count);
  btn.append(b);
  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    onClick();
  });
  return btn;
}

function renderTravelLine(stop: Stop, direction: "arrive" | "depart"): HTMLElement {
  const details = direction === "arrive" ? stop.arriveDetails : stop.departDetails;
  const mode = direction === "arrive" ? stop.arriveBy : stop.departBy;
  const time = direction === "arrive" ? details?.arrivalTime : details?.departureTime;

  const line = document.createElement("div");
  line.className = "xp-line";
  const roleIcon = document.createElement("span");
  roleIcon.className = "role-transit";
  roleIcon.appendChild(icon(mode ? TRAVEL_MODE_ICON_NAME[mode] : "arrow-right", { size: 15 }));
  line.append(roleIcon);

  line.append(`${i18n.t(direction === "arrive" ? "stopArrivalLabel" : "stopDepartureLabel")} `);

  if (time) {
    const b = document.createElement("b");
    b.className = "num";
    b.textContent = time;
    line.append(b);
  }
  const bits: string[] = [];
  if (details?.carrier) bits.push(details.carrier);
  if (details?.ticketRef) bits.push(details.ticketRef);
  if (bits.length) {
    const muted = document.createElement("span");
    muted.className = "muted";
    muted.textContent = ` · ${bits.join(" · ")}`;
    line.append(muted);
  }
  return line;
}

function renderStopMeta(stop: Stop, prev: Stop | null): HTMLElement | null {
  const dateText = formatStopDates(stop.dateFrom, stop.dateTo);
  const hasModes = stop.arriveBy || stop.departBy;
  const legRow = prev ? renderLegRow(prev, stop) : null;
  if (!dateText && !hasModes && !legRow) return null;

  const meta = document.createElement("span");
  meta.className = "stop-meta";

  if (legRow) meta.appendChild(legRow);

  if (dateText || hasModes) {
    const main = document.createElement("span");
    main.className = "stop-meta-main";
    if (dateText) {
      main.appendChild(icon("calendar", { size: 13 }));
      const text = document.createElement("span");
      text.className = "stop-meta-text";
      text.textContent = dateText;
      main.appendChild(text);
    }
    if (hasModes) {
      if (dateText) {
        const dot = document.createElement("span");
        dot.className = "stop-meta-sep";
        dot.textContent = "·";
        main.appendChild(dot);
      }
      const modes = document.createElement("span");
      modes.className = "stop-meta-modes";
      if (stop.arriveBy) {
        modes.appendChild(icon(TRAVEL_MODE_ICON_NAME[stop.arriveBy], { size: 13 }));
      }
      if (stop.arriveBy && stop.departBy) {
        modes.appendChild(icon("arrow-right", { size: 12 }));
      }
      if (stop.departBy) {
        modes.appendChild(icon(TRAVEL_MODE_ICON_NAME[stop.departBy], { size: 13 }));
      }
      main.appendChild(modes);
    }
    meta.appendChild(main);
  }

  return meta;
}

function renderLegRow(from: Stop, to: Stop): HTMLElement | null {
  const distanceKm = haversineKm(from, to);
  if (!Number.isFinite(distanceKm) || distanceKm <= 0) return null;

  const km = Math.round(distanceKm);
  const legMode = to.arriveBy ?? from.departBy;
  const minutes = estimateMinutes(distanceKm, legMode);

  const row = document.createElement("span");
  row.className = "stop-meta-leg";
  row.appendChild(icon("arrow-down", { size: 12 }));

  if (legMode) {
    row.appendChild(icon(TRAVEL_MODE_ICON_NAME[legMode], { size: 12 }));
  }

  const distanceText = document.createElement("span");
  distanceText.textContent = `${km} km`;
  row.appendChild(distanceText);

  if (minutes) {
    const sep = document.createElement("span");
    sep.className = "stop-meta-sep";
    sep.textContent = "·";
    row.appendChild(sep);
    const time = document.createElement("span");
    time.textContent = formatDuration(minutes);
    row.appendChild(time);
  }

  return row;
}

function formatStopDates(from: string | undefined, to: string | undefined): string {
  const fmt = new Intl.DateTimeFormat(i18n.current(), {
    day: "numeric",
    month: "short",
  });
  const fromDate = parseISODateLocal(from);
  const toDate = parseISODateLocal(to);
  if (fromDate && toDate) {
    if (from === to) return fmt.format(fromDate);
    return `${fmt.format(fromDate)} – ${fmt.format(toDate)}`;
  }
  if (fromDate) return fmt.format(fromDate);
  if (toDate) return fmt.format(toDate);
  return "";
}

function parseISODateLocal(value: string | undefined): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

function iconButton(name: IconName, label: string): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "icon-btn";
  btn.appendChild(icon(name, { size: 16 }));
  btn.title = label;
  btn.setAttribute("aria-label", label);
  return btn;
}

function setupSidebarToggle(): void {
  const toggle = document.querySelector<HTMLButtonElement>("#sidebar-toggle");
  const scrim = document.querySelector<HTMLElement>("#sidebar-scrim");
  if (!toggle || !scrim) return;

  const isOpen = (): boolean => document.body.classList.contains("sidebar-open");

  const setOpen = (open: boolean): void => {
    document.body.classList.toggle("sidebar-open", open);
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    scrim.hidden = !open;
    toggle.dataset.i18nTitle = open ? "drawerClose" : "drawerOpen";
    const titleKey = open ? "drawerClose" : "drawerOpen";
    toggle.title = i18n.t(titleKey);
    toggle.setAttribute("aria-label", i18n.t(titleKey));
  };

  toggle.addEventListener("click", () => setOpen(!isOpen()));
  scrim.addEventListener("click", () => setOpen(false));

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && isOpen()) {
      setOpen(false);
    }
  });

  window.addEventListener("resize", () => {
    if (window.innerWidth >= DRAWER_BREAKPOINT && isOpen()) {
      setOpen(false);
    }
  });

  i18n.addEventListener("change", () => setOpen(isOpen()));

  setOpen(false);
}

function exportTrip(): void {
  const json = stops.serialize();
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `trip-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function initTripName(): void {
  const display = required<HTMLParagraphElement>("#trip-name");

  function refresh(): void {
    const name = stops.getTripName();
    if (name) {
      display.textContent = name;
      display.classList.remove("trip-name--empty");
    } else {
      display.textContent = i18n.t("tripNamePlaceholder");
      display.classList.add("trip-name--empty");
    }
  }

  function startEdit(): void {
    const input = document.createElement("input");
    input.type = "text";
    input.className = "trip-name-input";
    input.value = stops.getTripName();
    input.placeholder = i18n.t("tripNamePlaceholder");
    display.replaceWith(input);
    input.focus();
    input.select();

    function commit(): void {
      stops.setTripName(input.value);
      input.replaceWith(display);
      refresh();
    }

    input.addEventListener("blur", commit);
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        input.blur();
      } else if (e.key === "Escape") {
        input.removeEventListener("blur", commit);
        input.replaceWith(display);
      }
    });
  }

  display.addEventListener("dblclick", startEdit);

  stops.addEventListener("change", (e) => {
    const detail = (e as CustomEvent).detail as { kind: string };
    if (detail.kind === "load" || detail.kind === "rename") refresh();
  });

  i18n.addEventListener("change", refresh);

  refresh();
}

function required<T extends Element>(selector: string): T {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`Element not found: ${selector}`);
  return el;
}
